import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    UrlTree,
    createUrlTreeFromSnapshot
} from '@angular/router';
import {
    LoggedInUser,
    UserService,
    UserStatus
} from '@flyfreely-portal-ui/flyfreely';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';

/**
 * This guard ensures the `organisationId` query parameter is present.
 *
 * If `organisationId` is already set, navigation proceeds unchanged.
 * If it is absent, the guard waits for the user to be logged in and then
 * redirects to the same path with `organisationId` set to
 * `currentUser.defaultOrganisationId ?? currentUser.personalOrganisationId`.
 *
 * Primarially for use with the main Workspace routing tree.
 */
@Injectable()
export class OrganisationRedirectGuard {
    constructor(private userService: UserService) {}

    async canActivate(
        route: ActivatedRouteSnapshot
    ): Promise<boolean | UrlTree> {
        if (
            route.queryParams.organisationId != null &&
            !isNaN(parseInt(route.queryParams.organisationId))
        ) {
            return true;
        }

        const loggedInUser = await firstValueFrom(
            this.userService.userChange$.pipe(
                filter(
                    (c): c is LoggedInUser => c.type === UserStatus.LOGGED_IN
                )
            )
        );

        const organisationId =
            loggedInUser.currentUser.defaultOrganisationId ??
            loggedInUser.currentUser.personalOrganisationId;

        return createUrlTreeFromSnapshot(route, [], {
            ...route.queryParams,
            organisationId
        });
    }
}
